'use client';

/**
 * Add Schedule Form Widget — canvas form for PDF → :Intent ingest.
 *
 * @component_role Canvas widget mounted when the caller clicks "Add
 *   Schedule" in the intent explorer. Collects a PDF + optional buyer
 *   context (project, schedule name, designer brief, room label) and
 *   fires `schedule_ingest`. On success the caller can load the new
 *   schedule into the grid canvas in one click.
 *
 * @business_rule Mirrors the Add Product flow in discovery: dedicated
 *   tab, capability-gated entry, back-to-explorer button, success card
 *   with "Load grid / Ingest another / Done" actions.
 *
 *   Fields collected:
 *     - project       (required — dropdown from useProjects)
 *     - pdf file      (required — base64, max 10MB)
 *     - schedule_name (optional — default derived from filename)
 *     - room_label    (optional — e.g. "hotel lobby lounge")
 *     - designer_brief (optional — free-text ~500 chars)
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain discovery (intent / ingest)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, CheckCircle, FileText, FileUp, Loader2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils/cn';
import {
  useProjects,
  useScheduleIngest,
  useIntentGrid,
} from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useTabStore } from '@/lib/stores/tab-store';
import type { CanvasWidgetProps } from '@/lib/registry/types';
import type { ScheduleIngestResponse } from '@/lib/api/types';

const MAX_PDF_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_BRIEF_CHARS = 500;

export function AddScheduleFormWidget(_props: CanvasWidgetProps) {
  // Capabilities
  const projectsStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const ingestStrategy = useCapabilityStore((s) => s.strategyName('intent', 'ingest_schedule'));
  const gridStrategy = useCapabilityStore((s) => s.strategyName('intent', 'grid'));

  const { data: projectsData, isLoading: projectsLoading } = useProjects(
    projectsStrategy ?? 'project_list',
    { limit: 100 },
  );
  const ingest = useScheduleIngest(ingestStrategy ?? 'schedule_ingest');
  const grid = useIntentGrid(gridStrategy ?? 'intent_grid');

  const setGridResponse = useIntentStore((s) => s.setGridResponse);
  const consumeScheduleFile = useIntentStore((s) => s.consumeScheduleFile);
  const closeTab = useTabStore((s) => s.closeTab);

  // Form state
  const [projectSid, setProjectSid] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [fileData, setFileData] = useState<string | null>(null); // base64
  const [scheduleName, setScheduleName] = useState('');
  const [roomLabel, setRoomLabel] = useState('');
  const [designerBrief, setDesignerBrief] = useState('');

  // Outcome
  const [result, setResult] = useState<ScheduleIngestResponse | null>(null);

  // Drag state
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const projects = (projectsData?.projects ?? []) as Array<{
    semantic_id?: string;
    name?: string;
  }>;

  // Drop-to-form handoff: if the user dropped a PDF on the intent
  // canvas, consume the staged file once on mount. The store clears
  // itself on consume so a later manual button click opens the form
  // empty. Runs before the filename-default effect so schedule_name
  // auto-fills too.
  useEffect(() => {
    const pending = consumeScheduleFile();
    if (!pending) return;
    setFile(pending.file);
    setFileData(pending.fileData);
  }, [consumeScheduleFile]);

  // Default schedule name tracks the filename when the user hasn't typed one.
  useEffect(() => {
    if (!file) return;
    if (scheduleName.trim()) return;
    const stem = file.name.replace(/\.[^/.]+$/, '').trim();
    setScheduleName(stem);
  }, [file, scheduleName]);

  const acceptFile = useCallback(async (f: File) => {
    if (f.size > MAX_PDF_BYTES) {
      toast.error(`File too large (${(f.size / 1024 / 1024).toFixed(1)} MB > 10 MB)`);
      return;
    }
    const isPdf = /pdf/i.test(f.type) || /\.pdf$/i.test(f.name);
    if (!isPdf) {
      toast.error('Only PDF schedules are supported in v1.');
      return;
    }
    const buf = await f.arrayBuffer();
    const b64 = btoa(
      new Uint8Array(buf).reduce((s, b) => s + String.fromCharCode(b), ''),
    );
    setFile(f);
    setFileData(b64);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const dropped = e.dataTransfer.files?.[0];
      if (dropped) await acceptFile(dropped);
    },
    [acceptFile],
  );

  const handleSubmit = () => {
    if (!projectSid) {
      toast.error('Pick a project first.');
      return;
    }
    if (!fileData || !file) {
      toast.error('Attach a PDF schedule.');
      return;
    }
    ingest.mutate(
      {
        project_sid: projectSid,
        file_data: fileData,
        file_name: file.name,
        schedule_name: scheduleName.trim() || null,
        room_label: roomLabel.trim() || null,
        designer_brief: designerBrief.trim() || null,
      },
      {
        onSuccess: (data) => {
          setResult(data);
          toast.success(`Ingested ${data.lines.length} lines — ${data.intent_sid}`);
        },
        onError: (err) => toast.error(`Ingest failed: ${err.message}`),
      },
    );
  };

  const handleLoadGrid = () => {
    if (!result) return;
    grid.mutate(
      {
        intent_sid: result.intent_sid,
        queries: result.lines.map((l) => ({ line_sid: l.line_sid, text: l.name })),
        top_k_per_line: 5,
      },
      {
        onSuccess: (data) => {
          setGridResponse(data);
          closeTab('add-schedule-form');
          toast.success('Grid loaded.');
        },
        onError: (err) => toast.error(`Grid failed: ${err.message}`),
      },
    );
  };

  const handleReset = () => {
    setFile(null);
    setFileData(null);
    setScheduleName('');
    setRoomLabel('');
    setDesignerBrief('');
    setResult(null);
  };

  // ── Success pane ──────────────────────────────────────────────────

  if (result) {
    return (
      <div className="h-full overflow-auto">
        <TabBar onBack={() => closeTab('add-schedule-form')} />
        <div className="max-w-xl mx-auto p-6 space-y-4">
          <div className="flex items-start gap-3 p-4 border border-success/30 bg-success/5 rounded-lg">
            <CheckCircle className="w-5 h-5 text-success mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Schedule ingested</p>
              <p className="text-xs text-muted-foreground">
                {result.lines.length} lines · intent {result.intent_sid}
                {result.low_confidence_count > 0 && (
                  <> · {result.low_confidence_count} low-confidence</>
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={handleLoadGrid}
              disabled={grid.isPending}
              className={cn(
                'w-full px-3 py-2 text-sm rounded-md',
                'bg-primary text-primary-foreground hover:bg-primary/90',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'flex items-center justify-center gap-2',
              )}
            >
              {grid.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Load grid
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="w-full px-3 py-2 text-sm rounded-md border border-border hover:bg-accent/50"
            >
              Ingest another
            </button>
            <button
              type="button"
              onClick={() => closeTab('add-schedule-form')}
              className="w-full px-3 py-2 text-sm rounded-md text-muted-foreground hover:text-foreground"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form pane ─────────────────────────────────────────────────────

  const briefCount = designerBrief.length;
  const canSubmit = !!projectSid && !!fileData && !ingest.isPending;

  return (
    <div className="h-full overflow-auto">
      <TabBar onBack={() => closeTab('add-schedule-form')} />
      <div className="max-w-xl mx-auto p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold">Add a schedule</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Drop a PDF; optional context sharpens the room brief.
          </p>
        </div>

        {/* Project */}
        <Field label="Project" required>
          <select
            value={projectSid}
            onChange={(e) => setProjectSid(e.target.value)}
            disabled={projectsLoading}
            className={cn(
              'w-full px-3 py-2 text-sm rounded-md',
              'bg-background border border-input',
              'focus:outline-none focus:ring-1 focus:ring-ring',
              'disabled:opacity-50',
            )}
          >
            <option value="">
              {projectsLoading ? 'Loading projects…' : 'Select a project'}
            </option>
            {projects.map((p) => (
              <option key={p.semantic_id ?? ''} value={p.semantic_id ?? ''}>
                {p.name ?? p.semantic_id}
              </option>
            ))}
          </select>
        </Field>

        {/* PDF drop */}
        <Field label="Schedule PDF" required>
          {file ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card">
              <FileText className="w-4 h-4 text-primary" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{file.name}</p>
                <p className="text-[10px] text-muted-foreground">
                  {(file.size / 1024).toFixed(0)} KB
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  setFileData(null);
                }}
                className="p-1 rounded hover:bg-muted text-muted-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={cn(
                'flex flex-col items-center justify-center gap-2',
                'px-3 py-6 rounded-md border-2 border-dashed',
                'cursor-pointer transition-colors',
                dragActive
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground',
              )}
            >
              <FileUp className="w-6 h-6" />
              <p className="text-xs">Drop a PDF, or click to browse</p>
              <p className="text-[10px] text-muted-foreground">Max 10 MB</p>
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) acceptFile(f);
                }}
                className="hidden"
              />
            </div>
          )}
        </Field>

        {/* Schedule name (optional) */}
        <Field label="Schedule name" hint="Defaults to the filename if left blank.">
          <input
            type="text"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            placeholder="e.g. Hotel Macau Lobby FF&E v3"
            className="w-full px-3 py-2 text-sm rounded-md bg-background border border-input focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </Field>

        {/* Room label (optional) */}
        <Field label="Room / space" hint="Sharpens the inferred setting.">
          <input
            type="text"
            value={roomLabel}
            onChange={(e) => setRoomLabel(e.target.value)}
            placeholder="e.g. hotel lobby lounge, guest suite bedroom"
            className="w-full px-3 py-2 text-sm rounded-md bg-background border border-input focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </Field>

        {/* Designer brief (optional) */}
        <Field
          label="Designer brief"
          hint="Short narrative. Feeds the aggregate room brief directly."
        >
          <textarea
            value={designerBrief}
            onChange={(e) =>
              setDesignerBrief(e.target.value.slice(0, MAX_BRIEF_CHARS))
            }
            rows={3}
            placeholder='e.g. "Dark, moody lobby — navy leanings, smoked oak, tweed, curvilinear"'
            className="w-full px-3 py-2 text-sm rounded-md bg-background border border-input focus:outline-none focus:ring-1 focus:ring-ring resize-none"
          />
          <div className="flex justify-end text-[10px] text-muted-foreground mt-1">
            {briefCount}/{MAX_BRIEF_CHARS}
          </div>
        </Field>

        {/* Submit */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={cn(
            'w-full px-3 py-2 text-sm rounded-md',
            'bg-primary text-primary-foreground hover:bg-primary/90',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'flex items-center justify-center gap-2',
          )}
        >
          {ingest.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          {ingest.isPending ? 'Ingesting…' : 'Ingest schedule'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Building blocks
// ============================================================

function TabBar({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex items-center px-3 py-2 border-b border-border bg-background/80">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back
      </button>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
